import React, { useEffect, useState } from "react";

// Modules
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";

// App
import axios from "axios";

import { apiEndpoints, serverUrl } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { patchNode, postNode } from "../../core/postNode";
import { deleteRequest } from "../../core/delete";
import PageHeader from "../../partials/pageHeader";
import AlertModal from "../../partials/alertModal";
import Error from "../../partials/error";
import {
  buildRoomPayload,
  milestonesFromRoom,
  sectionsFromRoom,
} from "./salesRoomData";
import SalesRoomCanvas from "./salesRoomCanvas";
import RoomThread from "../room/roomThread";

// UI components
import { Col, Container, Row } from "react-bootstrap";
import {
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/ShareOutlined";
import OpenNewIcon from "@mui/icons-material/OpenInNewOutlined";
import InsightsIcon from "@mui/icons-material/InsightsOutlined";
import MessageIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import CompanyappLoader from "../../partials/companyappLoader";
import VideoRecorder from "./videoRecorder.js";

const emptyForm = {
  title: "",
  description: "",
  company: "",
  status: "draft",
  expiry: "",
  colour: "",
  dashboardTitle: "",
  // Client logo (masthead right) and the seller's own (masthead left).
  logoFid: null,
  logoUrl: null,
  companyLogoFid: null,
  companyLogoUrl: null,
  contacts: [],
  sections: [],
  milestones: [],
  introVideoMid: null,
  introVideoName: null,
  introText: "",
  alertMode: "first_open",
  dailyDigest: false,
  isTemplate: false,
  hasPassword: false,
  password: "",
  accessMode: "link",
  passwordDirty: false,
};

export default function SalesRoomEdit() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [shareToken, setShareToken] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showMessages, setShowMessages] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const { id } = useParams();
  const user = useSelector((state) => state).authReducer.user;
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getRoom();
    // eslint-disable-next-line
  }, [id]);

  const getRoom = () => {
    getOnlineNode(`${apiEndpoints.salesRooms}/${id}?_format=json`)
      .then((response) => {
        hydrate(response.data);
        setIsLoading(false);
      })
      .catch((_error) => {
        setIsError(true);
        setErrorStatus(_error.response?.status || 0);
        setErrorMessage(_error.response?.statusText || "Failed to load room");
        setIsLoading(false);
      });
  };

  const hydrate = (room) => {
    setShareToken(room.access_token || null);
    setCanDelete(!!room.has_delete_permission);
    setForm({
      title: room.title || "",
      description: room.summary || "",
      company: room.field_company || "",
      status: room.room_status || "draft",
      expiry: room.field_expiry
        ? moment.unix(room.field_expiry).format("YYYY-MM-DDTHH:mm")
        : "",
      dashboardTitle: room.field_dashboard_title || "",
      colour: room.field_branding_colour || "",
      logoFid: room.field_branding_logo_id || null,
      logoUrl: room.field_branding_logo || null,
      companyLogoFid: room.field_company_logo_id || null,
      companyLogoUrl: room.field_company_logo || null,
      contacts: room.field_contacts || [],
      sections: sectionsFromRoom(room),
      milestones: milestonesFromRoom(room),
      introVideoMid: room.field_intro_video_id || null,
      introVideoName: room.field_intro_video_name || null,
      introText: room.field_intro_text || "",
      alertMode: room.field_alert_mode || "first_open",
      dailyDigest: !!room.field_daily_digest,
      isTemplate: !!room.is_template,
      hasPassword: !!room.has_password,
      password: room.field_password || "",
      accessMode: room.access_mode || "link",
      passwordDirty: false,
    });
  };

  const saveRoom = () => {
    if (!form.title.trim()) {
      enqueueSnackbar("Please enter a room name", { variant: "warning" });
      return;
    }

    setIsSaving(true);
    const payload = buildRoomPayload(form);

    // Publishing templates is permission-gated; the backend also enforces
    // this, but don't send the flag at all without the permission.
    if (permissions?.manage_sales_room_templates !== true) {
      delete payload.is_template;
    }

    patchNode(`${apiEndpoints.salesRooms}/${id}`, payload, user.csrf_token)
      .then((response) => {
        hydrate(response.data);
        setIsSaving(false);
        enqueueSnackbar("Room saved", { variant: "success" });
      })
      .catch((_error) => {
        setIsSaving(false);
        enqueueSnackbar(
          _error.response?.data?.message || "Failed to save the room",
          { variant: "error" }
        );
      });
  };

  const getMessages = () => {
    getOnlineNode(
      `${apiEndpoints.salesRooms}/${id}/messages?_format=json`
    )
      .then((response) => {
        setMessages(response.data.rows || []);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (showMessages) {
      getMessages();
    }
    // eslint-disable-next-line
  }, [showMessages]);

  const sendMessage = (body, attachments = []) => {
    return postNode(
      `${apiEndpoints.salesRooms}/${id}/messages`,
      { body, attachments },
      user.csrf_token
    ).then((response) => {
      setMessages((prev) => [...prev, response.data]);
    });
  };

  // Seller upload endpoint (authenticated). Returns the attachment descriptor.
  const uploadRoomFile = (file) => {
    const form = new FormData();
    form.append("file", file);
    return axios
      .post(`${serverUrl}/api/v3/sales-room/${id}/files`, form, {
        withCredentials: true,
        headers: { "X-CSRF-Token": user.csrf_token },
      })
      .then((response) => response.data);
  };

  // Attachments stream through the token-scoped buyer route; the seller has the
  // room's share token, so the same URL works from the dashboard.
  const attachmentUrl = (fid) =>
    shareToken ? `${serverUrl}/api/v3/room/${shareToken}/files/${fid}` : "";

  const deleteRoom = () => {
    setShowDeleteConfirm(false);
    setIsSaving(true);

    deleteRequest(`${apiEndpoints.salesRooms}/${id}?_format=json`)
      .then(() => {
        enqueueSnackbar("Room deleted.", { variant: "success" });
        navigate("/sales-rooms");
      })
      .catch(() => {
        setIsSaving(false);
        enqueueSnackbar("Failed to delete the room.", { variant: "error" });
      });
  };

  const sendAccessLink = (email) => {
    postNode(
      `${apiEndpoints.salesRooms}/${id}/access-link`,
      { email },
      user.csrf_token
    )
      .then(() => {
        enqueueSnackbar(`Access link sent to ${email}.`, {
          variant: "success",
        });
      })
      .catch((_error) => {
        enqueueSnackbar(
          _error.response?.data?.message ||
            "Could not send the access link. Save the room first so the contact is on the access list.",
          { variant: "error" }
        );
      });
  };

  const shareLink = shareToken
    ? `${window.location.origin}/room/${shareToken}`
    : null;

  const copyShareLink = () => {
    if (!shareLink) {
      return;
    }
    let message =
      "Share link copied. Anyone with this link can view the room while it is active - no login required.";
    if (form.accessMode === "password" && form.password) {
      message = `Share link copied. Buyers will also need the passcode "${form.password}" - share it separately.`;
    } else if (form.accessMode === "restricted") {
      message =
        "Share link copied. Only your buyer contacts can enter - they'll verify their email via a secure link sent to their inbox.";
    }

    navigator.clipboard
      .writeText(shareLink)
      .then(() => {
        enqueueSnackbar(message, { variant: "success" });
      })
      .catch(() => {
        enqueueSnackbar("Could not copy the share link", { variant: "error" });
      });
  };

  if (isError) {
    return <Error status={errorStatus} message={errorMessage} />;
  }


  return (
    <main className="edit-sales-room sales-rooms screen">
      <PageHeader pageName="Edit Sales Room" filters={false} />
      {isLoading ? (
        <CompanyappLoader />
      ) : (
        <Container className="px-0 mt-3">
          <Row className="mb-3 align-items-center">
            <Col xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                className="sales-room-toolbar"
              >
                {/* Left: leave + destructive, low-emphasis and kept away from
                    the primary Save so they can't be hit by accident. */}
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ textTransform: "none" }}
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/sales-rooms")}
                >
                  Back
                </Button>
                {canDelete && (
                  <Button
                    variant="text"
                    color="error"
                    sx={{ textTransform: "none" }}
                    startIcon={<DeleteIcon />}
                    disabled={isSaving}
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                )}

                {/* Rule doubles as the spacer that pushes the tools right. */}
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ ml: "auto", my: 0.5, display: { xs: "none", md: "block" } }}
                />

                {/* Right: room tools as light text buttons, then Save. */}
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ textTransform: "none" }}
                  startIcon={<SettingsIcon />}
                  onClick={() => setShowSettings(true)}
                >
                  Settings
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ textTransform: "none" }}
                  startIcon={<InsightsIcon />}
                  onClick={() => navigate(`/sales-rooms/view/${id}`)}
                >
                  Analytics
                </Button>
                <Button
                  variant="text"
                  color={showMessages ? "secondary" : "inherit"}
                  sx={{ textTransform: "none" }}
                  startIcon={<MessageIcon />}
                  onClick={() => setShowMessages(!showMessages)}
                >
                  {showMessages ? "Hide messages" : "Messages"}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ textTransform: "none" }}
                  startIcon={<OpenNewIcon />}
                  disabled={!shareLink}
                  onClick={() => window.open(`${shareLink}?preview=1`, "_blank")}
                >
                  Preview
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ textTransform: "none" }}
                  startIcon={<ShareIcon />}
                  disabled={!shareLink}
                  onClick={copyShareLink}
                >
                  Share link
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  className="action"
                  sx={{ textTransform: "none", ml: 1 }}
                  startIcon={isSaving ? null : <SaveIcon />}
                  disabled={isSaving}
                  onClick={saveRoom}
                >
                  {isSaving ? <CircularProgress size={20} /> : "Save"}
                </Button>
              </Stack>
            </Col>
          </Row>
          {form.status !== "active" && (
            <Row className="mb-3">
              <Col xs={12}>
                <Typography owner="admin" variant="body2" color="warning.main">
                  This room is {form.status} - buyers get a "room not
                  available" message until you set it to Active.
                </Typography>
              </Col>
            </Row>
          )}
          {showMessages && (
            <Row className="mb-4">
              <Col xs={12} lg={8} className="sales-room-edit__thread">
                <Typography owner="admin" variant="h6" className="mb-2">
                  Room messages
                </Typography>
                <RoomThread
                  messages={messages}
                  onSend={sendMessage}
                  mine="seller"
                  owner="admin"
                  uploadFile={uploadRoomFile}
                  attachmentUrl={attachmentUrl}
                  canUpload={true}
                  emptyText="No messages yet. Buyers can start a conversation from the room."
                />
              </Col>
            </Row>
          )}
          <SalesRoomCanvas
            form={form}
            setForm={setForm}
            onSendAccessLink={sendAccessLink}
            showSettings={showSettings}
            setShowSettings={setShowSettings}
          />

          {showDeleteConfirm && (
            <AlertModal
              showAlert={showDeleteConfirm}
              showAlertCallback={setShowDeleteConfirm}
              showCaLogo={true}
              alertMessageTitle={`Delete "${form.title}"?`}
              alertMessageBody={
                form.isTemplate
                  ? "This template will be removed from the library for all sellers. This can't be undone."
                  : "The room, its share link, and all of its analytics, messages and visitor data will be permanently deleted. Buyers with the link will no longer be able to access it. This can't be undone."
              }
              cancelButton={true}
              confirmButton={true}
              confirmButtonLabel="Delete"
              onConfirm={deleteRoom}
            />
          )}
        </Container>
      )}
    </main>
  );
}
